<script setup>
/**
 * Fork overlay (new file, no OSS equivalent) — EVA-M6.
 * Specialist-scenario manager for one assistant: lists scenarios, adds one (title + instruction; the
 * backend resolves tool:// references), toggles enabled, or deletes, via EvaScenariosAPI.
 * Tailwind-only; Composition API.
 */
import { reactive, ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import EvaScenariosAPI from 'dashboard/api/eva/scenarios';
import EvaToolCatalogAPI from 'dashboard/api/eva/toolCatalog';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  assistantId: { type: [Number, String], required: true },
});

const { t } = useI18n();

const scenarios = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const hasError = ref(false);
const draft = reactive({ title: '', instruction: '' });

// All tool ids referenceable as tool://id in an instruction (built-ins + enabled custom slugs) —
// shown as a helper so authors don't typo a reference (M7 7.2, B4).
const toolIds = ref([]);
// Tools the last save silently took away. The tools array is DERIVED from the instruction text, so
// deleting a tool:// line disables that capability with no other feedback anywhere — one account lost
// labelling for three weeks and read the result as the assistant misbehaving. The update response
// carries the diff; this is the only place the author can learn about it.
const removedTools = ref([]);
const editingId = ref(null);
const editDraft = reactive({ title: '', instruction: '', enabled: true });

const canAdd = computed(
  () =>
    draft.title.trim() !== '' &&
    draft.instruction.trim() !== '' &&
    !isSaving.value
);

const load = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await EvaScenariosAPI.getByAssistant(props.assistantId);
    scenarios.value = data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const add = async () => {
  if (!canAdd.value) return;
  isSaving.value = true;
  hasError.value = false;
  try {
    await EvaScenariosAPI.create({
      eva_assistant_id: props.assistantId,
      title: draft.title.trim(),
      instruction: draft.instruction,
    });
    Object.assign(draft, { title: '', instruction: '' });
    await load();
  } catch {
    hasError.value = true;
  } finally {
    isSaving.value = false;
  }
};

const toggle = async scenario => {
  try {
    await EvaScenariosAPI.update(scenario.id, { enabled: !scenario.enabled });
  } catch {
    hasError.value = true;
  } finally {
    await load(); // resync the list (and the one-way Switch) to server state
  }
};

const remove = async id => {
  try {
    await EvaScenariosAPI.delete(id);
  } catch {
    hasError.value = true;
  } finally {
    await load();
  }
};

const loadCatalog = async () => {
  try {
    const { data } = await EvaToolCatalogAPI.get();
    toolIds.value = [...data.built_in, ...data.custom];
  } catch {
    /* the helper is optional — a failed catalog load shouldn't break the panel */
  }
};

// --- inline edit (M7 7.2, B1) ---
const startEdit = scenario => {
  removedTools.value = [];
  editingId.value = scenario.id;
  Object.assign(editDraft, {
    title: scenario.title,
    instruction: scenario.instruction,
    enabled: scenario.enabled,
  });
};

const cancelEdit = () => {
  editingId.value = null;
};

// Amber for a tool:// reference that resolves to nothing (the runner drops it); muted otherwise (B4).
const toolChipClass = (scenario, tool) =>
  scenario.unresolved_tools?.includes(tool)
    ? 'text-n-amber-11 bg-n-amber-3'
    : 'text-n-slate-11 bg-n-alpha-black2';

const saveEdit = async id => {
  if (!editDraft.title.trim() || !editDraft.instruction.trim()) return;
  isSaving.value = true;
  hasError.value = false;
  try {
    const { data } = await EvaScenariosAPI.update(id, {
      title: editDraft.title.trim(),
      instruction: editDraft.instruction,
      enabled: editDraft.enabled,
    });
    removedTools.value = data?.removed_tools || [];
    editingId.value = null;
    await load();
  } catch {
    hasError.value = true;
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => {
  load();
  loadCatalog();
});

defineExpose({
  load,
  loadCatalog,
  add,
  toggle,
  remove,
  startEdit,
  cancelEdit,
  saveEdit,
  draft,
  editDraft,
  removedTools,
});
</script>

<template>
  <div class="flex flex-col gap-4 min-w-0">
    <h3 class="text-sm font-medium text-n-slate-12">
      {{ t('EVA_SCENARIOS.TITLE') }}
    </h3>

    <div
      class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
    >
      <Input
        v-model="draft.title"
        :label="t('EVA_SCENARIOS.NAME')"
        :placeholder="t('EVA_SCENARIOS.NAME_PLACEHOLDER')"
      />
      <TextArea
        v-model="draft.instruction"
        auto-height
        min-height="5rem"
        max-height="16rem"
        :label="t('EVA_SCENARIOS.INSTRUCTION')"
        :placeholder="t('EVA_SCENARIOS.INSTRUCTION_PLACEHOLDER')"
        :message="t('EVA_SCENARIOS.INSTRUCTION_HINT')"
      />
      <div v-if="toolIds.length" class="flex flex-col gap-1.5 min-w-0">
        <span class="text-xs text-n-slate-11">
          {{ t('EVA_SCENARIOS.TOOL_IDS') }}
        </span>
        <div class="flex flex-wrap gap-1 min-w-0">
          <code
            v-for="id in toolIds"
            :key="id"
            class="px-1.5 py-0.5 font-mono text-xs break-all rounded-md text-n-slate-11 bg-n-alpha-black2"
            >{{ `tool://${id}` }}</code
          >
        </div>
      </div>
      <div class="flex justify-end">
        <Button
          :label="t('EVA_SCENARIOS.ADD')"
          sm
          :is-loading="isSaving"
          :disabled="!canAdd"
          @click="add"
        />
      </div>
    </div>

    <div
      v-if="removedTools.length"
      class="flex items-start gap-2 px-3 py-2 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
    >
      <span class="i-lucide-alert-triangle size-4 mt-0.5 shrink-0" />
      <span class="flex-1">
        {{
          t('EVA_SCENARIOS.REMOVED_TOOLS_WARNING', {
            tools: removedTools.join(', '),
          })
        }}
      </span>
      <Button
        icon="i-lucide-x"
        amber
        xs
        ghost
        :aria-label="t('EVA_SCENARIOS.DISMISS')"
        @click="removedTools = []"
      />
    </div>

    <p v-if="hasError" class="text-sm text-n-ruby-11">
      {{ t('EVA_SCENARIOS.ERROR') }}
    </p>
    <p v-else-if="isLoading" class="text-sm text-n-slate-11">
      {{ t('EVA_SCENARIOS.LOADING') }}
    </p>
    <p
      v-else-if="!scenarios.length"
      class="px-6 py-6 text-sm text-center border border-dashed rounded-xl border-n-weak text-n-slate-11"
    >
      {{ t('EVA_SCENARIOS.EMPTY') }}
    </p>

    <ul v-else class="flex flex-col gap-3">
      <li
        v-for="scenario in scenarios"
        :key="scenario.id"
        class="flex flex-col gap-3 p-4 min-w-0 rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
      >
        <!-- edit form -->
        <template v-if="editingId === scenario.id">
          <Input v-model="editDraft.title" :label="t('EVA_SCENARIOS.NAME')" />
          <TextArea
            v-model="editDraft.instruction"
            auto-height
            min-height="5rem"
            max-height="16rem"
            :label="t('EVA_SCENARIOS.INSTRUCTION')"
          />
          <label class="flex items-center gap-2 text-sm text-n-slate-11">
            <Switch v-model="editDraft.enabled" />
            {{ t('EVA_SCENARIOS.ENABLED') }}
          </label>
          <div class="flex justify-end gap-2">
            <Button
              faded
              slate
              sm
              :label="t('EVA_SCENARIOS.CANCEL')"
              @click="cancelEdit"
            />
            <Button
              sm
              :label="t('EVA_SCENARIOS.SAVE')"
              :is-loading="isSaving"
              :disabled="
                isSaving ||
                !editDraft.title.trim() ||
                !editDraft.instruction.trim()
              "
              @click="saveEdit(scenario.id)"
            />
          </div>
        </template>

        <!-- static row -->
        <template v-else>
          <div class="flex items-center justify-between gap-3">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ scenario.title }}
            </span>
            <div class="flex items-center gap-2 shrink-0">
              <Switch
                :model-value="scenario.enabled"
                @change="toggle(scenario)"
              />
              <span class="w-px h-4 bg-n-weak" />
              <Button
                icon="i-lucide-pen"
                slate
                xs
                ghost
                :aria-label="t('EVA_SCENARIOS.EDIT')"
                @click="startEdit(scenario)"
              />
              <Button
                icon="i-lucide-trash"
                slate
                xs
                ghost
                :aria-label="t('EVA_SCENARIOS.DELETE')"
                @click="remove(scenario.id)"
              />
            </div>
          </div>
          <p
            v-if="scenario.instruction"
            class="text-sm break-words whitespace-pre-line text-n-slate-11 line-clamp-2"
          >
            {{ scenario.instruction }}
          </p>
          <div
            v-if="scenario.tools && scenario.tools.length"
            class="flex flex-wrap gap-1 min-w-0"
          >
            <code
              v-for="tool in scenario.tools"
              :key="tool"
              class="px-1.5 py-0.5 font-mono text-xs break-all rounded-md"
              :class="toolChipClass(scenario, tool)"
              >{{ `tool://${tool}` }}</code
            >
          </div>
          <p
            v-if="scenario.unresolved_tools?.length"
            class="text-xs text-n-amber-11"
          >
            {{ t('EVA_SCENARIOS.UNRESOLVED_WARNING') }}
          </p>
        </template>
      </li>
    </ul>
  </div>
</template>
